import {
  forwardRef,
  useMemo,
  type ReactElement,
  type Ref,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  Slide,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import { useSelfSetupTour } from "../../hooks/useSelfSetupTour";
import { SetupStepsProgress } from "./SetupStepsProgress";
import { APP_FONT_FAMILY } from "../../constants/fonts";

const SlideDown = forwardRef(function SlideDown(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const TITLE_SX = {
  fontFamily: APP_FONT_FAMILY,
  fontWeight: 600,
  color: "text.primary",
  lineHeight: 1.235,
  letterSpacing: "0.00735em",
} as const;

/**
 * Welcome / congrats dialog — visual match for Ops OrgSetupGate.
 */
export function SelfSetupGate() {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    showWelcomeHub,
    showCongrats,
    steps,
    nextIncomplete,
    allDone,
    startTour,
    goToStep,
    dismissTour,
    dismissCongrats,
  } = useSelfSetupTour();

  const accent = theme.palette.primary.main;
  const secondary = theme.palette.secondary.main;
  const isDark = theme.palette.mode === "dark";
  const completedCount = steps.filter((s) => s.done).length;
  const showRestoreCopy = completedCount > 0 && !allDone;

  const welcomeOpen = Boolean(showWelcomeHub && !allDone && nextIncomplete);
  const congratsOpen = Boolean(showCongrats);
  const dialogOpen = welcomeOpen || congratsOpen;

  const continueLabel = useMemo(() => {
    if (!nextIncomplete) return t("selfSetup.welcomeContinueAction", {
      step: t("selfSetup.steps.dashboard"),
    });
    return t("selfSetup.welcomeContinueAction", {
      step: t(`selfSetup.steps.${nextIncomplete}`),
    });
  }, [nextIncomplete, t]);

  const heroGradient = isDark
    ? `linear-gradient(155deg, ${alpha(accent, 0.7)} 0%, ${alpha(secondary, 0.32)} 48%, ${alpha(accent, 0.1)} 100%)`
    : `linear-gradient(155deg, ${alpha(accent, 0.5)} 0%, ${alpha(secondary, 0.28)} 48%, ${alpha(accent, 0.1)} 100%)`;

  const paperSx = {
    m: 0,
    width: "100%",
    border: 1,
    borderColor: alpha(accent, isDark ? 0.45 : 0.28),
    boxShadow: isDark
      ? `0 24px 64px ${alpha("#000", 0.55)}, 0 0 0 1px ${alpha(accent, 0.2)}`
      : `0 20px 56px ${alpha("#0f172a", 0.16)}, 0 0 0 1px ${alpha(accent, 0.12)}`,
    overflow: "hidden",
    position: "relative" as const,
    borderRadius: "14px",
    bgcolor: theme.palette.background.paper,
  };

  const dialogContainerSx = {
    "& .MuiDialog-container": {
      alignItems: "center",
      justifyContent: "center",
      px: 1.5,
    },
  };

  const backdropSx = {
    bgcolor: isDark
      ? alpha(theme.palette.common.black, 0.72)
      : alpha("#0f172a", 0.45),
  };

  const iconBadgeSx = {
    width: 80,
    height: 80,
    mx: "auto",
    mt: -5,
    mb: 2.5,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    bgcolor: isDark
      ? alpha(theme.palette.background.paper, 0.98)
      : theme.palette.background.paper,
    border: `2.5px solid ${accent}`,
    boxShadow: isDark
      ? `0 10px 28px ${alpha("#000", 0.45)}, 0 0 0 6px ${alpha(accent, 0.28)}`
      : `0 10px 28px ${alpha(accent, 0.28)}, 0 0 0 6px ${alpha(accent, 0.16)}`,
    color: accent,
  };

  return (
    <Dialog
      open={dialogOpen}
      disableEscapeKeyDown
      onClose={() => {
        /* Close only via Later / primary CTA — no X or backdrop dismiss */
      }}
      TransitionComponent={SlideDown}
      keepMounted={false}
      maxWidth={false}
      slotProps={{
        backdrop: {
          sx: backdropSx,
        },
      }}
      sx={dialogContainerSx}
      PaperProps={{
        sx: { ...paperSx, maxWidth: 600 },
      }}
    >
      <Box
        aria-hidden
        sx={{
          height: 100,
          background: heroGradient,
        }}
      />

      <Box
        sx={{
          position: "relative",
          px: { xs: 3.5, sm: 5 },
          pb: 4.5,
          textAlign: "center",
        }}
      >
        <Box sx={iconBadgeSx}>
          {congratsOpen ? (
            <CelebrationOutlinedIcon sx={{ fontSize: 38 }} />
          ) : (
            <RocketLaunchOutlinedIcon sx={{ fontSize: 38 }} />
          )}
        </Box>

        <Typography
          variant="h4"
          sx={{
            ...TITLE_SX,
            mb: congratsOpen ? 3.25 : 1,
            fontSize: { xs: "1.45rem", sm: "1.65rem" },
          }}
        >
          {congratsOpen
            ? t("selfSetup.congratsTitle")
            : t(
                showRestoreCopy
                  ? "selfSetup.welcomeRestoreTitle"
                  : "selfSetup.welcomeTitle",
              )}
        </Typography>

        {!congratsOpen ? (
          <Typography
            variant="body1"
            sx={{
              fontFamily: APP_FONT_FAMILY,
              mb: 3.25,
              fontSize: "1rem",
              lineHeight: 1.6,
              maxWidth: 420,
              mx: "auto",
              color: "text.primary",
              opacity: 0.78,
            }}
          >
            {t(
              showRestoreCopy
                ? "selfSetup.welcomeRestoreSubtitle"
                : "selfSetup.welcomeSubtitle",
            )}
          </Typography>
        ) : null}

        <SetupStepsProgress
          steps={steps}
          accent={accent}
          labelFor={(key) => t(`selfSetup.steps.${key}`)}
          highlightCurrent={!congratsOpen}
          onStepClick={
            congratsOpen
              ? undefined
              : (key) => {
                  if (!steps.find((s) => s.key === key)?.done) {
                    goToStep(key);
                  }
                }
          }
        />

        {congratsOpen ? (
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={dismissCongrats}
            sx={{
              fontFamily: APP_FONT_FAMILY,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              py: 1.5,
              fontSize: "1rem",
              boxShadow: "none",
              bgcolor: accent,
              color: theme.palette.primary.contrastText,
              "&:hover": {
                boxShadow: "none",
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            {t("selfSetup.congratsAction")}
          </Button>
        ) : nextIncomplete ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={startTour}
              sx={{
                fontFamily: APP_FONT_FAMILY,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                py: 1.5,
                fontSize: "1rem",
                boxShadow: "none",
                bgcolor: accent,
                color: theme.palette.primary.contrastText,
                "&:hover": {
                  boxShadow: "none",
                  bgcolor: theme.palette.primary.dark,
                },
              }}
            >
              {continueLabel}
            </Button>
            <Button
              variant="text"
              color="inherit"
              onClick={dismissTour}
              sx={{
                fontFamily: APP_FONT_FAMILY,
                textTransform: "none",
                color: "text.primary",
                fontWeight: 600,
                fontSize: "0.9rem",
                "&:hover": {
                  bgcolor: isDark
                    ? alpha(theme.palette.common.white, 0.08)
                    : alpha(accent, 0.08),
                },
              }}
            >
              {t("selfSetup.welcomeLaterAction")}
            </Button>
          </Box>
        ) : null}
      </Box>
    </Dialog>
  );
}

export default SelfSetupGate;
